'use client';

import * as Sentry from '@sentry/nextjs';
import { useEffect, useState } from 'react';
import {
  getPaddleUsageCharges,
  getPaddleSubscription,
  PaddleSubscriptionStatus,
  PaddleUsageCharge,
} from '@/entities/billing/api/paddle.service';

const USAGE_LABELS: Record<PaddleUsageCharge['usageType'], string> = {
  ocr: 'Image/PDF OCR',
  item_scheduling: 'Scheduled data updates',
  message_overage: 'Additional chatbot messages',
  storage_overage: 'Additional database storage',
  additional_agent: 'Additional agents',
  specialized_agent: 'Specialized agents',
  speech_stt_overage: 'Voice transcription overage',
  speech_tts_overage: 'Voice synthesis overage',
};

export const PaddlePendingUsageCheckout = () => {
  const [subscription, setSubscription] =
    useState<PaddleSubscriptionStatus | null>();
  const [usageCharges, setUsageCharges] = useState<PaddleUsageCharge[]>([]);

  useEffect(() => {
    let isMounted = true;

    getPaddleSubscription()
      .then((subscription) => {
        if (isMounted) {
          setSubscription(subscription);
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
      });
    getPaddleUsageCharges()
      .then((charges) => {
        if (isMounted) {
          setUsageCharges(charges);
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const pendingUsageCheckoutUrl = subscription?.pendingUsageCheckoutUrl;

  if (!pendingUsageCheckoutUrl && usageCharges.length === 0) {
    return null;
  }

  return (
    <aside className="mb-[24px] w-full max-w-[1080px] rounded-[16px] border border-gray-200 bg-white p-[20px] text-gray-900">
      {usageCharges.length > 0 && (
        <>
          <p className="text-body1 font-[700]">Usage charges</p>
          <p className="text-body2 mt-[4px] text-gray-500">
            Recent usage recorded for your Paddle subscription.
          </p>
          <div className="mt-[16px] overflow-x-auto">
            <table className="text-body2 w-full min-w-[620px] text-left">
              <thead className="border-b border-gray-200 text-gray-500">
                <tr>
                  <th className="px-[8px] py-[10px] font-[600]">Usage</th>
                  <th className="px-[8px] py-[10px] font-[600]">Agent</th>
                  <th className="px-[8px] py-[10px] font-[600]">Quantity</th>
                  <th className="px-[8px] py-[10px] font-[600]">Status</th>
                  <th className="px-[8px] py-[10px] font-[600]">
                    Billing period ends
                  </th>
                </tr>
              </thead>
              <tbody>
                {usageCharges.map((charge, index) => (
                  <tr
                    className="border-b border-gray-100 last:border-b-0"
                    key={`${charge.usageType}-${charge.periodEnd}-${index}`}
                  >
                    <td className="px-[8px] py-[12px] font-[600]">
                      {USAGE_LABELS[charge.usageType]}
                    </td>
                    <td className="px-[8px] py-[12px]">
                      {charge.agentName ?? '—'}
                    </td>
                    <td className="px-[8px] py-[12px]">{charge.quantity}</td>
                    <td className="px-[8px] py-[12px] capitalize">
                      {charge.status}
                    </td>
                    <td className="px-[8px] py-[12px]">
                      {new Intl.DateTimeFormat('en-US').format(
                        new Date(charge.periodEnd)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {pendingUsageCheckoutUrl && (
        <div className="main:flex-row main:items-center main:justify-between mt-[16px] flex flex-col gap-[12px] rounded-[12px] border border-orange-200 bg-orange-50 p-[16px] text-orange-950">
          <div>
            <p className="text-body1 font-[700]">Outstanding usage payment</p>
            <p className="text-body2 mt-[4px]">
              Complete this payment to settle usage charges from your canceled
              subscription.
            </p>
          </div>
          <a
            className="text-body2 flex h-[44px] shrink-0 items-center justify-center rounded-[10px] bg-orange-600 px-[20px] font-[700] text-white hover:bg-orange-700"
            href={pendingUsageCheckoutUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
            Pay outstanding usage
          </a>
        </div>
      )}
    </aside>
  );
};
